import { z } from 'zod';

const host = z
  .string()
  .trim()
  .max(255)
  .regex(/^[a-z0-9.-]*$/i, 'Некоректна адреса сервера')
  .transform((v) => v || null);
const port = z.number().int().min(1).max(65535);
const text = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => v || null);

/** Налаштування пошти в довіднику. Секрети: не передано — не змінювати, '' — прибрати. */
export const mailSettingsSchema = z.object({
  smtpHost: host,
  smtpPort: port,
  smtpSecure: z.boolean(),
  smtpUser: text(255),
  smtpPassword: z.string().max(500).optional(),
  fromName: text(200),
  fromEmail: z
    .email('Некоректний email')
    .max(255)
    .or(z.literal(''))
    .transform((v) => v || null),
  imapHost: host,
  imapPort: port,
  imapSecure: z.boolean(),
  mailDomain: z
    .string()
    .trim()
    .toLowerCase()
    .max(255)
    .regex(/^[a-z0-9.-]*$/, 'Некоректний домен')
    .transform((v) => v || null),
  apiToken: z.string().trim().max(500).optional(),
  hostingAccountId: z
    .string()
    .trim()
    .max(50)
    .regex(/^\d*$/, 'ID акаунта — лише цифри')
    .default('')
    .transform((v) => v || null),
});
export type MailSettingsInput = z.input<typeof mailSettingsSchema>;

/** Що робити зі скринькою при звільненні; за замовчуванням — закрити доступ, листи лишаються. */
export const DISMISS_MAILBOX_ACTIONS = ['keep', 'block', 'delete'] as const;

export const mailboxLoginSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[a-z0-9][a-z0-9._-]{0,62}[a-z0-9]$/, 'Логін: латиниця, цифри, крапка, дефіс');

export const mailboxConnectSchema = z.object({
  userId: z.uuid(),
  address: z.email('Некоректна адреса').max(255),
  password: z.string().max(200).optional(),
  /** Встановити новий пароль через хостинг замість введення поточного. */
  resetPassword: z.boolean().default(false),
});

export const SIGNATURE_IMAGE_POSITIONS = ['left', 'below'] as const;
/** Найбільший розмір картинки підпису: вона йде в кожному листі. */
export const SIGNATURE_IMAGE_MAX_BYTES = 512 * 1024;

/** Підпис скриньки (рішення 033): назва, текст, картинка; один зі скриньки — за замовчуванням. */
export const mailSignatureSchema = z.object({
  name: z.string().trim().min(1, 'Вкажіть назву').max(60),
  html: z.string().max(20_000),
  imagePosition: z.enum(SIGNATURE_IMAGE_POSITIONS),
  imageWidth: z.number().int().min(32).max(300),
  isDefault: z.boolean(),
});
export type MailSignatureInput = z.infer<typeof mailSignatureSchema>;

/** Де шукати текст пошуку: поля листа (IMAP SEARCH). */
export const MAIL_SEARCH_FIELDS = ['subject', 'body', 'from', 'to', 'cc', 'bcc'] as const;
export type MailSearchField = (typeof MAIL_SEARCH_FIELDS)[number];
/** Без вибору — як звичайний пошук: усе, крім прихованої копії. */
export const MAIL_SEARCH_DEFAULT_FIELDS: MailSearchField[] = [
  'subject',
  'body',
  'from',
  'to',
  'cc',
];
export const MAIL_SEARCH_TYPES = ['unread', 'read', 'flagged', 'answered', 'unanswered'] as const;
/** Розмір листа, більший за (МБ). */
export const MAIL_SEARCH_SIZES = [1, 5, 10, 20] as const;

const isoDay = z.iso.date();

/** Тонке налаштування пошуку в поштовику. Порожні поля — без обмеження. */
export const mailSearchSchema = z.object({
  fields: z.array(z.enum(MAIL_SEARCH_FIELDS)).min(1).max(MAIL_SEARCH_FIELDS.length).optional(),
  type: z.enum(MAIL_SEARCH_TYPES).optional(),
  /** Отримано з цього дня (включно) / до цього дня (включно), `yyyy-MM-dd`. */
  after: isoDay.optional(),
  before: isoDay.optional(),
  attachments: z.boolean().optional(),
  /** Більший за стільки МБ. */
  larger: z.number().int().min(1).max(100).optional(),
  /** В усіх папках, а не лише у вибраній. */
  everywhere: z.boolean().optional(),
  sort: z.enum(['newest', 'oldest']).optional(),
});
export type MailSearch = z.infer<typeof mailSearchSchema>;

/** Чи звужує налаштування пошуку список саме по собі (без тексту пошуку). */
export function mailSearchFilters(s: MailSearch) {
  return Boolean(s.type || s.after || s.before || s.attachments || s.larger);
}

/** Правила власної папки: нові листи «Вхідних», що підходять, переносяться в неї (рішення 031). */
export const MAIL_RULE_KINDS = ['from', 'sender', 'domain', 'subject', 'body', 'to'] as const;
export type MailRuleKind = (typeof MAIL_RULE_KINDS)[number];
const ruleValues = z.array(z.string().trim().toLowerCase().min(1).max(200)).max(100).default([]);
export const mailFolderRulesSchema = z.object({
  /** Від кого: точні адреси. */
  from: ruleValues,
  /** Відправник містить: частина імені чи адреси («Google Таблиці», «noreply»). */
  sender: ruleValues,
  /** Домен відправника, без «@». */
  domain: ruleValues.transform((v) => v.map((d) => d.replace(/^@/, ''))),
  /** Слово чи фраза в темі. */
  subject: ruleValues,
  /** Слово чи фраза в тексті листа (шукає поштовий сервер). */
  body: ruleValues,
  /** Кому або копія: точні адреси (наприклад, спільна скринька). */
  to: ruleValues,
  /** Лише з вкладеннями. */
  attachments: z.boolean().default(false),
  /** Розсилки й сповіщення: є заголовок List-Id або List-Unsubscribe. */
  lists: z.boolean().default(false),
  /** Більший за стільки МБ. */
  larger: z.number().int().min(1).max(100).nullable().default(null),
  /** Досить однієї умови чи потрібні всі. */
  match: z.enum(['any', 'all']).default('any'),
  /** Винятки: слово чи фраза в темі або тексті — лист лишається у «Вхідних». */
  except: ruleValues,
  /** Не переносити, а лише додати в папку: лист лишається у «Вхідних» і видно у вкладці. */
  keep: z.boolean().default(false),
});
export type MailFolderRules = z.infer<typeof mailFolderRulesSchema>;
export const emptyMailFolderRules = (): MailFolderRules => mailFolderRulesSchema.parse({});

/** Лист для перевірки правил: заголовки з копії скриньки. */
export type RuleMessage = {
  from: { name?: string; address: string }[];
  to: { address: string }[];
  cc: { address: string }[];
  subject: string;
  attachments?: boolean;
  /** Розмір, байти. */
  size?: number;
};

/**
 * Те, чого в заголовках немає, — відповідає поштовий сервер (IMAP SEARCH): чи є слово в
 * тексті, чи це розсилка. Без нього (наприклад, у браузері) такі умови вважаються невиконаними.
 */
export type RuleServerChecks = {
  body?: (word: string) => boolean;
  list?: boolean;
};

/** Чи підходить лист під правила папки. */
export function matchesMailRules(m: RuleMessage, r: MailFolderRules, srv: RuleServerChecks = {}) {
  const from = m.from.map((a) => a.address.toLowerCase());
  const names = m.from.map((a) => `${a.name ?? ''} ${a.address}`.toLowerCase());
  const to = [...m.to, ...m.cc].map((a) => a.address.toLowerCase());
  const subject = m.subject.toLowerCase();
  const inBody = (w: string) => srv.body?.(w) ?? false;
  if (r.except.some((w) => subject.includes(w) || inBody(w))) return false;
  const checks: boolean[] = [];
  if (r.from.length) checks.push(from.some((a) => r.from.includes(a)));
  if (r.sender.length) checks.push(names.some((n) => r.sender.some((w) => n.includes(w))));
  if (r.domain.length) {
    checks.push(from.some((a) => r.domain.some((d) => a.endsWith(`@${d}`) || a.endsWith(`.${d}`))));
  }
  if (r.subject.length) checks.push(r.subject.some((w) => subject.includes(w)));
  if (r.body.length) checks.push(r.body.some(inBody));
  if (r.to.length) checks.push(to.some((a) => r.to.includes(a)));
  if (r.attachments) checks.push(Boolean(m.attachments));
  if (r.lists) checks.push(Boolean(srv.list));
  if (r.larger) checks.push((m.size ?? 0) > r.larger * 1024 * 1024);
  if (!checks.length) return false;
  return r.match === 'all' ? checks.every(Boolean) : checks.some(Boolean);
}

/** Чи є в папки хоч одна умова (без умов — лише ручне перенесення). */
export const hasMailRules = (r: MailFolderRules) =>
  MAIL_RULE_KINDS.some((k) => r[k].length > 0) || r.attachments || r.lists || Boolean(r.larger);

/** Колір власної папки (назви — ключі палітри в інтерфейсі). */
export const MAIL_FOLDER_COLORS = [
  'red',
  'orange',
  'amber',
  'green',
  'teal',
  'sky',
  'blue',
  'violet',
  'pink',
  'gray',
  'black',
  'white',
] as const;
/** Колір папки: з палітри або будь-який з колірного круга (`#rrggbb`). */
export const mailFolderColorSchema = z.union([
  z.enum(MAIL_FOLDER_COLORS),
  z
    .string()
    .regex(/^#[0-9a-f]{6}$/i, 'Колір — у форматі #rrggbb')
    .transform((v) => v.toLowerCase() as `#${string}`),
]);
export type MailFolderColor = (typeof MAIL_FOLDER_COLORS)[number] | `#${string}`;

/**
 * Як фарбувати папку: `parts` — один колір для вибраних частин (`paint.parts`), `custom` —
 * свій колір для кожної частини. `all`, `icon`, `text`, `bubble` — збережені раніше (одна
 * частина або всі) — читаються як `parts`.
 */
export const MAIL_PAINT_MODES = ['parts', 'custom', 'all', 'icon', 'text', 'bubble'] as const;
export const MAIL_PAINT_PARTS = ['bubble', 'text', 'icon'] as const;
export type MailPaintPart = (typeof MAIL_PAINT_PARTS)[number];
export type MailPaintMode = (typeof MAIL_PAINT_MODES)[number];
/** Окремі кольори (режим `custom`); null — типовий для цієї частини. */
export const mailFolderPaintSchema = z.object({
  icon: mailFolderColorSchema.nullable().default(null),
  text: mailFolderColorSchema.nullable().default(null),
  bubble: mailFolderColorSchema.nullable().default(null),
  /** Частини, які фарбує один колір папки (режим `parts`). */
  parts: z.array(z.enum(MAIL_PAINT_PARTS)).max(3).optional(),
});
export type MailFolderPaint = z.infer<typeof mailFolderPaintSchema>;
/** Іконка власної папки (ключі — у наборі іконок інтерфейсу). */
export const MAIL_FOLDER_ICONS = [
  'folder',
  'star',
  'heart',
  'flag',
  'bookmark',
  'tag',
  'briefcase',
  'users',
  'user',
  'handshake',
  'file-text',
  'receipt',
  'banknote',
  'calendar',
  'clock',
  'megaphone',
  'bell',
  'graduation-cap',
  'building',
  'home',
  'truck',
  'package',
  'wrench',
  'shield',
  'stethoscope',
  'globe',
  'lightbulb',
  'archive',
] as const;
export type MailFolderIcon = (typeof MAIL_FOLDER_ICONS)[number];

/** Власна папка: назва (без роздільників ієрархії) і правила. */
export const mailFolderSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Вкажіть назву')
    .max(60)
    .regex(/^[^./\\]+$/, 'Назва без крапок і скісних рисок'),
  /** Усередині якої власної папки (шлях); null — верхній рівень; не вказано — не змінювати. */
  parent: z.string().min(1).max(500).nullable().optional(),
  /** Вигляд у панелі папок; null — типовий. */
  color: mailFolderColorSchema.nullable().optional(),
  paintMode: z.enum(MAIL_PAINT_MODES).optional(),
  paint: mailFolderPaintSchema.nullable().optional(),
  icon: z.enum(MAIL_FOLDER_ICONS).nullable().optional(),
  /** Без правил — проста папка: листи в неї додають вручну. */
  rules: mailFolderRulesSchema.prefault({}),
  /** Одразу перенести листи «Вхідних», що вже підходять під правила. */
  applyExisting: z.boolean().default(false),
});
export type MailFolderInput = z.input<typeof mailFolderSchema>;

// ─── Додаткові скриньки (рішення 032) ──────────────────────────────────────

export interface MailEndpoint {
  host: string;
  port: number;
  secure: boolean;
}

/** Сервери поштових сервісів за доменом адреси. `note` — що підказати людині. */
export interface MailProvider {
  name: string;
  imap: MailEndpoint;
  smtp: MailEndpoint;
  /** app-password — потрібен пароль застосунку (з двоетапною перевіркою), не звичайний пароль. */
  note?: 'app-password' | 'enable-imap';
}

const ssl = (host: string, port = 993): MailEndpoint => ({ host, port, secure: true });

const PROVIDERS: { domains: string[]; provider: MailProvider }[] = [
  {
    domains: ['gmail.com', 'googlemail.com'],
    provider: {
      name: 'Gmail',
      imap: ssl('imap.gmail.com'),
      smtp: ssl('smtp.gmail.com', 465),
      note: 'app-password',
    },
  },
  {
    domains: ['ukr.net'],
    provider: {
      name: 'ukr.net',
      imap: ssl('imap.ukr.net'),
      smtp: ssl('smtp.ukr.net', 465),
      note: 'app-password',
    },
  },
  {
    domains: ['i.ua'],
    provider: { name: 'I.UA', imap: ssl('imap.i.ua'), smtp: ssl('smtp.i.ua', 465) },
  },
  {
    domains: ['meta.ua'],
    provider: { name: 'META.UA', imap: ssl('imap.meta.ua'), smtp: ssl('smtp.meta.ua', 465) },
  },
  {
    domains: ['yahoo.com'],
    provider: {
      name: 'Yahoo',
      imap: ssl('imap.mail.yahoo.com'),
      smtp: ssl('smtp.mail.yahoo.com', 465),
      note: 'app-password',
    },
  },
  {
    domains: ['icloud.com', 'me.com', 'mac.com'],
    provider: {
      name: 'iCloud',
      imap: ssl('imap.mail.me.com'),
      smtp: { host: 'smtp.mail.me.com', port: 587, secure: false },
      note: 'app-password',
    },
  },
  {
    domains: ['outlook.com', 'hotmail.com', 'live.com', 'msn.com'],
    provider: {
      name: 'Outlook',
      imap: ssl('outlook.office365.com'),
      smtp: { host: 'smtp-mail.outlook.com', port: 587, secure: false },
      note: 'app-password',
    },
  },
];

/** Відомий сервіс за адресою; невідомий — null (тоді сервери вводять вручну або вгадуємо). */
export function mailProviderFor(address: string): MailProvider | null {
  const domain = address.split('@')[1]?.toLowerCase() ?? '';
  return PROVIDERS.find((p) => p.domains.includes(domain))?.provider ?? null;
}

/** Здогадка для невідомого домену: imap.<домен> / smtp.<домен>. */
export function guessMailServers(address: string): { imap: MailEndpoint; smtp: MailEndpoint } {
  const domain = address.split('@')[1]?.toLowerCase() ?? '';
  return { imap: ssl(`imap.${domain}`), smtp: ssl(`smtp.${domain}`, 465) };
}

const endpoint = z.object({
  host: z
    .string()
    .trim()
    .min(1)
    .max(255)
    .regex(/^[a-z0-9.-]+$/i, 'Некоректна адреса сервера'),
  port: z.number().int().min(1).max(65535),
  secure: z.boolean(),
});

/** Підключення додаткової скриньки: адреса, пароль і (необов'язково) сервери й логін. */
export const mailAccountSchema = z.object({
  address: z.email('Некоректна адреса').max(255).toLowerCase(),
  password: z.string().min(1, 'Вкажіть пароль').max(500),
  login: z.string().trim().max(255).optional(),
  imap: endpoint.optional(),
  smtp: endpoint.optional(),
});
export type MailAccountInput = z.infer<typeof mailAccountSchema>;
